import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Plus, Star, Store } from "lucide-react";
import { errorMessage } from "@web/lib/api";
import { useDebounced } from "@web/lib/useDebounced";
import { useNavAction } from "@web/lib/navAction";
import { venuesQuery } from "@web/lib/queries";
import { vendorCategoriesQuery, vendorsQuery, type Vendor } from "@web/lib/queries-vendors";
import {
  Button, ChipStrip, EmptyState, Inline, Page, Region, RowGroup, SearchBar,
  SelectChip, Skeleton, Stack, StatusChip, Text, Toolbar, Workspace,
} from "@web/ui";
import { VendorRow } from "./vendor/VendorRow";
import { VendorSheet } from "./vendor/VendorSheet";
import { NewVendorSheet } from "./vendor/NewVendorSheet";

/** More › Vendors (SPEC §6.1 item 13a, §3.11).
 *
 * The directory people actually use from a phone at an event: search, find the
 * florist, tap the number. Filters live in the URL so a refresh, the back button
 * or a link shared with a colleague all show the same list
 * (docs/plans/RESPONSIVE-STANDARD.md).
 *
 * Grouping is by category, because that is how somebody looks for a vendor they
 * cannot name. Filtering by venue re-sorts rather than hides: preferred at that
 * venue rises to the top with a star, and everybody else stays available,
 * because "preferred" is a recommendation and not a restriction.
 */
export type VendorsSearch = { q?: string; categoryId?: string; venueId?: string; preferred?: "1" };

export function Vendors() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/app/more/vendors" }) as VendorsSearch;
  const [draft, setDraft] = useState(search.q ?? "");
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  useNavAction({ label: "New vendor", icon: <Plus aria-hidden size={26} />, onClick: () => setAdding(true) });

  const setSearch = (next: VendorsSearch) =>
    navigate({ to: "/more/vendors", search: { ...search, ...next }, replace: true });
  const q = useDebounced(draft);
  const categories = useQuery(vendorCategoriesQuery);
  const venues = useQuery(venuesQuery());

  const filters = {
    ...(q ? { q } : {}),
    ...(search.categoryId ? { categoryId: search.categoryId } : {}),
    ...(search.venueId ? { venueId: search.venueId } : {}),
    ...(search.preferred === "1" && search.venueId ? { preferredOnly: "1" as const } : {}),
  };
  const list = useQuery(vendorsQuery(filters));
  const rows = list.data ?? [];

  /** Grouped by category, in the taxonomy's own order. A vendor with no category
   * lands in "Uncategorised" at the end rather than disappearing. */
  const groups = useMemo(() => {
    const order = new Map((categories.data ?? []).map((category, index) => [category.id, index]));
    const byId = new Map((categories.data ?? []).map(category => [category.id, category.name]));
    const buckets = new Map<string, { name: string; sort: number; rows: Vendor[] }>();
    for (const vendor of rows) {
      const key = vendor.categoryId ?? "";
      const bucket = buckets.get(key) ?? {
        name: byId.get(key) ?? vendor.categoryName ?? "Uncategorised",
        sort: order.get(key) ?? Number.MAX_SAFE_INTEGER,
        rows: [],
      };
      bucket.rows.push(vendor);
      buckets.set(key, bucket);
    }
    return [...buckets.values()].sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name));
  }, [rows, categories.data]);

  /** The taxonomy is deliberately large — a venue should not have to invent
   * "Draping & lighting" before it can file its first vendor — but a filter strip
   * of forty-seven chips is a wall. Only the categories this company has actually
   * filed something under are offered, plus whichever one is selected. */
  const inUse = (categories.data ?? []).filter(category => category.vendorCount > 0 || category.id === search.categoryId);

  const venueName = (venues.data ?? []).find(venue => venue.id === search.venueId)?.name;

  return <Page variant="collection">
    <Stack gap={3}>
      <Toolbar>
        <SearchBar value={draft} onChange={value => { setDraft(value); setSearch({ q: value || undefined }); }}
          placeholder="Search vendors" label="Search vendors" />
      </Toolbar>

      <ChipStrip label="Venue" wrap>
        <SelectChip selected={!search.venueId} onClick={() => setSearch({ venueId: undefined, preferred: undefined })}>
          Every venue
        </SelectChip>
        {(venues.data ?? []).map(venue => (
          <SelectChip key={venue.id} selected={search.venueId === venue.id} onClick={() => setSearch({ venueId: venue.id })}>
            {venue.shortName ?? venue.name}
          </SelectChip>
        ))}
      </ChipStrip>
      {search.venueId && (
        <ChipStrip label={`At ${venueName ?? "this venue"}`} wrap>
          <SelectChip selected={search.preferred !== "1"} onClick={() => setSearch({ preferred: undefined })}>
            Everybody
          </SelectChip>
          <SelectChip selected={search.preferred === "1"} onClick={() => setSearch({ preferred: "1" })}>
            Preferred here only
          </SelectChip>
        </ChipStrip>
      )}
      {inUse.length > 0 && (
        <ChipStrip label="Category" wrap>
          <SelectChip selected={!search.categoryId} onClick={() => setSearch({ categoryId: undefined })}>All</SelectChip>
          {inUse.map(category => (
            <SelectChip key={category.id} selected={search.categoryId === category.id} onClick={() => setSearch({ categoryId: category.id })}>
              {category.name}
            </SelectChip>
          ))}
        </ChipStrip>
      )}

      {list.isError
        ? <EmptyState icon={Store} title="Vendors could not be loaded" body={errorMessage(list.error)}
            action={<Button onClick={() => void list.refetch()}>Try again</Button>} />
        : !list.data ? <Skeleton variant="cards" count={3} />
          : rows.length === 0
            ? <EmptyState icon={Store} title={q || search.categoryId || search.preferred ? "No matches" : "No vendors yet"}
                body={q || search.categoryId || search.preferred
                  ? "Try a name, a category, or clear the filters."
                  : "Add the caterers, florists and DJs you work with, and they will be one tap away from any booking."}
                action={<Button fullWidth={false} onClick={() => setAdding(true)}>Add a vendor</Button>} />
            : <Workspace variant="overview">
              {groups.map((group, index) => (
                <Region key={group.name} area={index % 2 === 0 ? "main" : "aside"} label={group.name}>
                  <Stack gap={2}>
                    <Inline justify="between" align="center">
                      <Text as="h3" variant="title">{group.name}</Text>
                      <StatusChip tone="neutral">{group.rows.length}</StatusChip>
                    </Inline>
                    <RowGroup>
                      {group.rows.map(vendor => (
                        <VendorRow key={vendor.id} vendor={vendor} onOpen={() => setOpen(vendor.id)}
                          showStar={Boolean(search.venueId) && vendor.standing === "preferred"} />
                      ))}
                    </RowGroup>
                  </Stack>
                </Region>
              ))}
            </Workspace>}

      {search.venueId && search.preferred === "1" && rows.length > 0 && (
        <Inline gap={2} align="center">
          <Star aria-hidden size={16} />
          <Text variant="caption" tone="tertiary">Preferred at {venueName ?? "this venue"}. Every other vendor is still available on a booking.</Text>
        </Inline>
      )}
    </Stack>

    {open && <VendorSheet vendorId={open} onClose={() => setOpen(null)} />}
    <NewVendorSheet open={adding} onClose={() => setAdding(false)} onCreated={id => { setAdding(false); setOpen(id); }} />
  </Page>;
}
